import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Music2, Code2, Settings } from 'lucide-react';

interface BytebeatLayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { href: '/player', label: 'Player', icon: Music2 },
  { href: '/editor', label: 'Editor', icon: Code2 },
  { href: '/settings', label: 'Settings', icon: Settings },
];

export function BytebeatLayout({ children }: BytebeatLayoutProps) {
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center group-hover:border-primary/60 transition-colors">
              <Music2 className="w-4 h-4 text-primary" />
            </div>
            <span className="font-bold text-lg tracking-tight glow-cyan text-primary">
              Bytebeat
            </span>
          </Link>

          <nav className="flex items-center gap-1">
            {navItems.map(({ href, label, icon: Icon }) => (
              <Link
                key={href}
                to={href}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200',
                  pathname === href || pathname.startsWith(href)
                    ? 'bg-primary/15 text-primary border border-primary/30'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60',
                )}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden sm:block">{label}</span>
              </Link>
            ))}
          </nav>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-6">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-border/30 py-3 text-center text-xs text-muted-foreground/50">
        <a
          href="https://shakespeare.diy"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-muted-foreground transition-colors"
        >
          Vibed with Shakespeare
        </a>
      </footer>
    </div>
  );
}
